<template>
  <div>
    <HeaderRoutinePrint :routineExecution="routineExecution" />
    <DetailRoutinePrint
      :routineExecution="routineExecution"
      :checklistItems="checklistItems"
    />
    <div class="d-flex justify-center">
      <span
        class="pa-0 my-4 body-2 font-weight-bold blue-grey--text text--darken-3"
      >
        {{
          $t('routinesPrint.completed_findings_title', {
            number: totalCompletedFindings,
          })
        }}
      </span>
    </div>
    <v-progress-linear
      v-if="isLoading"
      indeterminate
      color="blue"
      class="mt-6"
    ></v-progress-linear>
    <div v-else class="mt-2">
      <div
        v-for="finding in findings"
        :key="finding.id"
        class="d-flex justify-start flex-column"
      >
        <p
          class="caption font-weight-bold blue-grey--text text--darken-3 pa-0 my-0 mr-1 "
        >
          {{ `${$t('routinesPrint.completed_finding_name')}:` }}

          <span class="caption blue-grey--text text--darken-3">
            {{ finding.situation }}
          </span>
        </p>
        <p
          class="caption font-weight-bold blue-grey--text text--darken-3 pa-0 my-0 mr-1 mb-1"
        >
          {{ `${$t('routinesPrint.completed_finding_solution')}:` }}
          <span class="caption blue-grey--text text--darken-3">
            {{ findingSolution(finding.revId) }}
          </span>
        </p>
        <div class="d-flex">
          <div class="d-flex flex-column" style="width: 50%">
            <p
              class="caption font-weight-bold blue-grey--text text--darken-3 pa-0 my-0 mr-1 "
            >
              {{ `${$t('common.assignee_other')}:` }}

              <span class="caption blue-grey--text text--darken-3">
                {{ assignees(finding.assignees) }}
              </span>
            </p>
            <p
              class="caption font-weight-bold blue-grey--text text--darken-3 pa-0 my-0 mr-1 "
            >
              {{ $t('common.status') }}:
              <span class="caption blue-grey--text text--darken-3">
                {{ getStatusText(finding.status) }}
              </span>
            </p>
            <p
              class="caption font-weight-bold blue-grey--text text--darken-3 pa-0 my-0 mr-1 "
            >
              {{ `${$t('common.due_date')}:` }}
              <span class="caption blue-grey--text text--darken-3">
                {{ formatFindingDate(finding.dueDate) }}
              </span>
            </p>
          </div>

          <div class="d-flex flex-column">
            <p
              class="caption font-weight-bold blue-grey--text text--darken-3 pa-0 my-0 mr-1 "
            >
              {{ `${$t('common.priority')}:` }}

              <span class="caption blue-grey--text text--darken-3">
                {{ findingPriority(finding.priority) }}
              </span>
            </p>
            <p
              class="caption font-weight-bold blue-grey--text text--darken-3 pa-0 my-0 mr-1 "
            >
              {{ `${$t('common.category_other')}:` }}
              <span class="caption blue-grey--text text--darken-3">
                {{ findingCategories(finding.categories) }}
              </span>
            </p>
            <p
              class="caption font-weight-bold blue-grey--text text--darken-3 pa-0 my-0 mr-1 "
            >
              {{ `${$t('routinesPrint.completed_finding_date')}:` }}
              <span class="caption blue-grey--text text--darken-3">
                {{ formatFindingDate(finding.completedAt) }}
              </span>
            </p>
          </div>
        </div>
        <v-divider class="theme--print mb-2 mt-1"></v-divider>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import api from '@/api'
import _ from 'lodash'
import mixin from '@/mixins/mixin.vue'
import userMixin from '@/mixins/userMixin'
import { utils } from 'frontend-js-shared'

import HeaderRoutinePrint from './HeaderRoutinePrint.vue'
import DetailRoutinePrint from './DetailRoutinePrint.vue'

const revTypes = {
  quickAction: 'quickAction',
}

export default {
  name: 'CompletedFindingsDetailPage',

  mixins: [mixin, userMixin],

  data() {
    return {
      solutions: [],
      revTypes,
      isLoading: true,
    }
  },

  components: {
    HeaderRoutinePrint,
    DetailRoutinePrint,
  },

  props: {
    findings: {
      type: Array,
      required: true,
    },
    routineExecution: {
      type: Object,
      required: true,
    },
    totalCompletedFindings: {
      type: Number,
    },
    checklistItems: {
      type: Array,
    },
    numberOfFindingsChunk: Number,
    totalPages: Number,
  },

  mounted() {
    this.fetchRevs()
  },

  computed: {
    ...mapGetters(['organizationId']),

    hasPages() {
      return Boolean(this.totalPages > 0)
    },
  },

  methods: {
    assignees(assignees) {
      const assigneesNumber = assignees.length
      if (assigneesNumber > 1) {
        return `${this.getFullName(assignees[0])}, +${assigneesNumber - 1}`
      }
      return this.getFullName(assignees[0])
    },
    getStatusText(status) {
      return this.$t(`routines.status.${_.snakeCase(status)}`)
    },
    findingPriority(priority) {
      const parsedPriority = !priority ? 'none' : priority
      return this.$t(`rev.priority.${parsedPriority}`)
    },
    findingCategories(categories) {
      if (categories.length > 1) {
        return `${this.$t(
          `common.categories.${categories[0]}`
        )}, +${categories.length - 1}`
      }

      return _.isEmpty(categories)
        ? ''
        : this.$t(`common.categories.${categories[0]}`)
    },
    formatFindingDate(date) {
      if (!date) return
      return utils.dates.formatDate(date, 'MMM D, YYYY')
    },

    findingSolution(revId) {
      return (
        this.solutions.find(solution => solution.revId === revId)?.solution ??
        this.$t('routinesPrint.deleted_finding')
      )
    },

    async fetchRevs() {
      try {
        const findingsRevs = this.findings
          .map(finding => finding.revId)
          .filter(revId => !_.isEmpty(revId))

        if (_.isEmpty(findingsRevs)) return

        const response = await api.explore.getContent({
          organizationId: this.organizationId,
          filters: {
            types: [
              this.$constants.exploreEntityTypes.improvementIdea,
              this.$constants.exploreEntityTypes.problemSolving,
              this.$constants.exploreEntityTypes.quickAction,
            ],
            id: findingsRevs,
          },
        })

        const revs =
          response?.data?.results?.map(result => result.response) ?? []

        this.solutions = revs.map(rev => ({
          revId: rev.id,
          solution:
            rev.entityType === this.revTypes.quickAction
              ? rev.afterPhase?.solution
              : rev.expand?.solution,
        }))
      } catch (err) {
        this.showError(err)
      } finally {
        this.isLoading = false
      }
    },
  },
}
</script>
<style lang="scss" scoped>
.pages {
  position: absolute;
  bottom: 0;
  right: 0;
  width: 100%;
  font-size: 10px;
}
</style>
